import React from 'react';
import {graphql, useFragment} from 'react-relay';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {describeBoundWithEnd, describePattern} from './describeTaskSet';
import {taskSetColour} from './taskSetColours';

/**
 * Legacy scoping-page chrome rather than design-system tokens, for the same reason
 * the drawer frame is: this sits directly above the task table and has to read as
 * part of it. A v2-themed card wedged between the toolbar and the table would look
 * like a different product bolted on.
 */
const Panel = styled.section`
	flex-shrink: 0;
	margin: 0 24px 8px 24px;
	border: 1px solid #e6e6e6;
	border-radius: 4px;
	background: #ffffff;
	font-size: 13px;
	color: rgba(0, 0, 0, 0.87);
	/* The panel pushes the table down rather than covering it, so it must not grow
	   without limit on a project with many sets. */
	max-height: 320px;
	overflow-y: auto;
`;

const PanelTitle = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.87);
	padding: 12px 12px 2px 12px;
`;

/** Ties the row to the colour its instances' chips carry in the table below. */
const Swatch = styled.span<{colour: string}>`
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 3px;
	background: ${({colour}) => colour};
	margin-right: 8px;
	flex-shrink: 0;
`;

const NameWithSwatch = styled.span`
	display: inline-flex;
	align-items: center;
`;

const Table = styled.table`
	width: 100%;
	border-collapse: collapse;
`;

const HeaderCell = styled.th`
	text-align: left;
	font-size: 10px;
	font-weight: 600;
	letter-spacing: 0.5px;
	text-transform: uppercase;
	color: #7d7d7d;
	padding: 8px 12px;
	border-bottom: 1px solid #e6e6e6;
	white-space: nowrap;
`;

const NumericHeaderCell = styled(HeaderCell)`
	text-align: right;
`;

const Row = styled.tr`
	height: 30px;

	&:not(:last-child) > td {
		border-bottom: 1px solid #f3f3f3;
	}
`;

const Cell = styled.td`
	padding: 6px 12px;
	vertical-align: middle;
	font-size: 12px;
	color: #535353;
`;

/** The row's anchor, so it carries the panel's full size and weight. */
const NameCell = styled(Cell)`
	font-size: 13px;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.87);
`;

/** Right-aligned with tabular figures so the counts line up down the column. */
const NumericCell = styled(Cell)`
	text-align: right;
	font-variant-numeric: tabular-nums;
`;

/** Non-zero exceptions are the thing worth noticing, so only they take colour. */
const ExceptionCell = styled(NumericCell)<{hasExceptions: boolean}>`
	color: ${({hasExceptions}) => (hasExceptions ? '#e57700' : '#535353')};
	font-weight: ${({hasExceptions}) => (hasExceptions ? 500 : 400)};
`;

const ActionCell = styled(Cell)`
	text-align: right;
	white-space: nowrap;
`;

const ViewButton = styled.button<{colour: string}>`
	border: 0;
	background: transparent;
	padding: 2px 4px;
	font-size: 12px;
	font-weight: 600;
	color: ${({colour}) => colour};
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}

	&:focus-visible {
		outline: 2px solid ${({colour}) => colour};
		outline-offset: 1px;
	}
`;

const EmptyState = styled.div`
	padding: 16px 12px;
	color: #7d7d7d;
`;

/** Shown in place of a phrase this version cannot describe, never a guess. */
const Unknown = styled.span`
	color: #a1a1a1;
`;

interface TaskSetRow {
	readonly id: string;
	readonly name?: string | null;
	readonly frequency?: string | null;
	readonly intervalLength?: number | null;
	readonly weekdays?: readonly (string | null)[] | null;
	readonly monthlyWeekday?: string | null;
	readonly monthlyMode?: string | null;
	readonly dayOfMonth?: number | null;
	readonly weekOrdinal?: number | null;
	readonly boundType?: string | null;
	readonly boundCount?: number | null;
	readonly boundEndDate?: string | null;
	readonly materialisedTo?: string | null;
	readonly instanceCount?: number | null;
	readonly exceptionCount?: number | null;
}

export interface ManageTaskSetsPanelProps {
	/** Owned by the page, alongside the other task-set surfaces' open state. */
	isExpanded: boolean;
	project: unknown;
	/** Opens the read-only config view, the same surface the instance chip opens. */
	onViewSet: (taskSetId: string) => void;
}

const panelFragment = graphql`
	fragment ManageTaskSetsPanel_project on ProjectType {
		taskSets(first: 1000) {
			edges {
				node {
					id
					name
					frequency
					intervalLength
					weekdays
					monthlyWeekday
					monthlyMode
					dayOfMonth
					weekOrdinal
					boundType
					boundCount
					boundEndDate
					materialisedTo
					instanceCount
					exceptionCount
				}
			}
		}
	}
`;

/**
 * The project's task sets, listed between the toolbar and the task table.
 *
 * This exists because a set needs a home that does not depend on one of its
 * instances being on screen. The instances may all be complete, filtered out, or
 * inside a phase the user has collapsed -- and then the chip on an instance, the
 * only other way to reach a set, is unreachable.
 *
 * One row per SET, never a container wrapping the instance rows. Wrapping the rows
 * would reintroduce the collapsible group header by another route, which was
 * rejected: instances are ordinary tasks and have to keep behaving like the rows
 * around them.
 *
 * There is no estimate column. The estimate is stamped onto each instance at
 * creation and is not stored on the set, so there is no single value here to show
 * -- and instances can diverge, which is what the exception count is for.
 */
export const ManageTaskSetsPanel = ({isExpanded, project, onViewSet}: ManageTaskSetsPanelProps) => {
	const intl = useIntl();
	const data = useFragment(panelFragment, project as never) as {
		taskSets?: {edges?: ReadonlyArray<{node?: TaskSetRow | null} | null> | null} | null;
	} | null;

	// Collapsed costs nothing on a page with no sets, which is every page until the
	// feature is used.
	if (!isExpanded) {
		return null;
	}

	const sets: TaskSetRow[] = (data?.taskSets?.edges || [])
		.map(edge => edge?.node)
		.filter((node): node is TaskSetRow => !!node);

	if (sets.length === 0) {
		return (
			<Panel data-cy="manage-task-sets-panel">
				<EmptyState>{intl.formatMessage({id: 'task_set.manage_empty'})}</EmptyState>
			</Panel>
		);
	}

	const unknown = <Unknown>{intl.formatMessage({id: 'task_set.none'})}</Unknown>;

	return (
		<Panel data-cy="manage-task-sets-panel">
			<PanelTitle>{intl.formatMessage({id: 'task_set.panel_title'})}</PanelTitle>
			<Table>
				<thead>
					<tr>
						<HeaderCell>{intl.formatMessage({id: 'task_set.set_column'})}</HeaderCell>
						<HeaderCell>{intl.formatMessage({id: 'task_set.pattern'})}</HeaderCell>
						<HeaderCell>{intl.formatMessage({id: 'task_set.bound_column'})}</HeaderCell>
						<NumericHeaderCell>{intl.formatMessage({id: 'task_set.exceptions'})}</NumericHeaderCell>
						<HeaderCell />
					</tr>
				</thead>
				<tbody>
					{sets.map(set => {
						const pattern = describePattern(intl, set);
						const bound = describeBoundWithEnd(intl, set);
						const colour = taskSetColour(set.id);
						const exceptions = set.exceptionCount ?? 0;
						return (
							<Row key={set.id} data-cy="manage-task-sets-row">
								<NameCell>
									<NameWithSwatch>
										<Swatch colour={colour.background} aria-hidden="true" />
										{set.name}
									</NameWithSwatch>
								</NameCell>
								<Cell>{pattern === null ? unknown : pattern}</Cell>
								<Cell>{bound === null ? unknown : bound}</Cell>
								<ExceptionCell hasExceptions={exceptions > 0}>{exceptions}</ExceptionCell>
								<ActionCell>
									<ViewButton
										type="button"
										colour={colour.foreground}
										onClick={() => onViewSet(set.id)}
										data-cy="manage-task-sets-view"
									>
										{intl.formatMessage({id: 'task_set.view'})}
									</ViewButton>
								</ActionCell>
							</Row>
						);
					})}
				</tbody>
			</Table>
		</Panel>
	);
};
